import { useState } from "react";
import { Copy, Check, Terminal, Smartphone, ExternalLink, Monitor, Apple } from "lucide-react";
import { toast } from "sonner";

export const IOS_APPS = [
  { name: "Locally AI", url: "https://apps.apple.com/app/locally-ai-private-ai-chat/id6741426692", note: "Recommandé · gratuit, par LM Studio, optimisé puces Apple", top: true },
  { name: "PocketPal AI", url: "https://apps.apple.com/app/pocketpal-ai/id6502579498", note: "Gratuit · importez le GGUF depuis Hugging Face" },
  { name: "LLM Farm", url: "https://apps.apple.com/app/llm-farm/id6461209061", note: "Open source · formats GGUF & GGML" },
  { name: "Private LLM", url: "https://apps.apple.com/app/private-llm-local-ai-chat/id6448106860", note: "Payant · modèles optimisés intégrés" },
];

const OS_LIST = [
  { id: "windows", label: "Windows", Icon: Monitor },
  { id: "mac", label: "macOS", Icon: Apple },
  { id: "linux", label: "Linux", Icon: Terminal },
];

export function detectOS() {
  const p = (navigator.userAgent || "").toLowerCase();
  if (p.includes("win")) return "windows";
  if (p.includes("mac") || p.includes("iphone") || p.includes("ipad")) return "mac";
  return "linux";
}

const TOOLS = {
  ollama: {
    name: "Ollama",
    site: "https://ollama.com",
    install: {
      windows: { link: "https://ollama.com/download/windows", text: "Téléchargez et lancez OllamaSetup.exe" },
      mac: { link: "https://ollama.com/download/mac", text: "Téléchargez l'app macOS (ou via Homebrew)", cmd: "brew install ollama" },
      linux: { link: "https://ollama.com/download/linux", text: "Installez via le script officiel", cmd: "curl -fsSL https://ollama.com/install.sh | sh" },
    },
    run: (m) => [{ label: "Télécharger et lancer le modèle", cmd: `ollama run ${m.ollama}` }],
  },
  lmstudio: {
    name: "LM Studio",
    site: "https://lmstudio.ai",
    install: {
      windows: { link: "https://lmstudio.ai/download?os=win32", text: "Téléchargez l'installeur Windows (interface graphique)" },
      mac: { link: "https://lmstudio.ai/download?os=mac", text: "Téléchargez l'app macOS (Apple Silicon recommandé)" },
      linux: { link: "https://lmstudio.ai/download?os=linux", text: "Téléchargez l'AppImage Linux" },
    },
    run: (m) => [
      { label: "Ou via la ligne de commande (lms)", cmd: `lms get ${m.hf}` },
    ],
    deepLink: (m) => `lmstudio://open_from_hf?model=${m.hf}`,
  },
  llamacpp: {
    name: "llama.cpp",
    site: "https://github.com/ggml-org/llama.cpp",
    install: {
      windows: { link: "https://github.com/ggml-org/llama.cpp/releases", text: "Via winget ou les binaires GitHub", cmd: "winget install llama.cpp" },
      mac: { link: "https://github.com/ggml-org/llama.cpp/releases", text: "Via Homebrew", cmd: "brew install llama.cpp" },
      linux: { link: "https://github.com/ggml-org/llama.cpp/releases", text: "Via Homebrew ou les binaires GitHub", cmd: "brew install llama.cpp" },
    },
    run: (m) => [
      { label: "Discuter en terminal", cmd: `llama-cli -hf ${m.hf}` },
      { label: "Ou lancer un serveur web local (http://localhost:8080)", cmd: `llama-server -hf ${m.hf}` },
    ],
  },
};

export function CmdLine({ label, cmd }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    toast.success("Commande copiée");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1">
      {label && <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>}
      <div className="flex items-center gap-2 rounded-md border border-[#1E2638] bg-[#080B10] px-3 py-2">
        <Terminal className="h-3.5 w-3.5 shrink-0 text-primary" />
        <code className="flex-1 overflow-x-auto whitespace-nowrap font-mono2 text-xs text-slate-200">{cmd}</code>
        <button onClick={copy} data-testid="install-command-copy-button" className="shrink-0 text-muted-foreground transition-colors hover:text-primary" aria-label="Copier">
          {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/60 font-mono2 text-[11px] text-primary">{n}</div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="text-sm font-medium text-slate-200">{title}</div>
        {children}
      </div>
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-[#1E2638] text-muted-foreground hover:text-slate-200"}`}
    >
      {children}
    </button>
  );
}

function ExtLink({ href, children, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
      {children} <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function IosGuide({ model }) {
  return (
    <div className="space-y-4" data-testid="install-guide-ios">
      <Step n={1} title="Installez une app depuis l'App Store">
        <div className="space-y-2">
          {IOS_APPS.map((a) => (
            <a key={a.name} href={a.url} target="_blank" rel="noopener noreferrer" data-testid="ios-app-link"
              className={`flex items-center justify-between rounded-md border bg-[#080B10] px-3 py-2 transition-colors hover:border-primary ${a.top ? "border-primary/70" : "border-[#1E2638]"}`}>
              <div>
                <div className="text-sm font-medium text-slate-200">{a.name}</div>
                <div className="text-[11px] text-muted-foreground">{a.note}</div>
              </div>
              <ExternalLink className="h-4 w-4 text-primary" />
            </a>
          ))}
        </div>
      </Step>
      <Step n={2} title="Dans Locally AI, ouvrez « Manage Models » et choisissez un modèle proche. Dans les autres apps, recherchez le GGUF :">
        <CmdLine cmd={`${model.hf}-GGUF`} />
      </Step>
      <Step n={3} title="Chargez-le et discutez, 100 % hors ligne" />
    </div>
  );
}

function DesktopGuide({ model, toolId, os }) {
  const tool = TOOLS[toolId];
  const inst = tool.install[os];
  return (
    <div className="space-y-4" data-testid={`install-guide-${toolId}`}>
      <Step n={1} title={`Installez ${tool.name}`}>
        <p className="text-xs text-muted-foreground">{inst.text}</p>
        <div className="flex flex-wrap gap-2">
          <ExtLink href={inst.link} testid="install-tool-download-link">Télécharger {tool.name}</ExtLink>
        </div>
        {inst.cmd && <CmdLine cmd={inst.cmd} />}
      </Step>
      <Step n={2} title={`Lancez ${model.name}`}>
        {tool.deepLink && (
          <a href={tool.deepLink(model)} data-testid="install-lmstudio-open-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-primary px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
            Ouvrir directement dans LM Studio <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {tool.run(model).map((r) => <CmdLine key={r.cmd} label={r.label} cmd={r.cmd} />)}
      </Step>
      <Step n={3} title="C'est prêt">
        <p className="text-xs text-muted-foreground">
          Le premier lancement télécharge le modèle (plusieurs Go). Ensuite, tout tourne en local, sans connexion.{" "}
          <a href={tool.site} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Documentation {tool.name}</a>
        </p>
      </Step>
    </div>
  );
}

export function InstallCommands({ model }) {
  const isMobile = (model.use_cases || []).includes("mobile");
  const [os, setOs] = useState(detectOS);
  const [tool, setTool] = useState("ollama");
  const tools = [...Object.entries(TOOLS).map(([id, t]) => ({ id, name: t.name })), ...(isMobile ? [{ id: "ios", name: "iPhone" }] : [])];

  return (
    <div className="space-y-4" data-testid="install-guide">
      <div className="flex flex-wrap gap-1.5">
        {tools.map((t) => (
          <Pill key={t.id} active={tool === t.id} onClick={() => setTool(t.id)} testid={`install-tool-tab-${t.id}`}>
            {t.id === "ios" && <Smartphone className="h-3.5 w-3.5" />}{t.name}
          </Pill>
        ))}
      </div>
      {tool !== "ios" && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-[#1E2638] pt-3">
          <span className="mr-1 text-[11px] uppercase tracking-wide text-muted-foreground">Système</span>
          {OS_LIST.map(({ id, label, Icon }) => (
            <Pill key={id} active={os === id} onClick={() => setOs(id)} testid={`install-os-tab-${id}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </Pill>
          ))}
        </div>
      )}
      {tool === "ios" ? <IosGuide model={model} /> : <DesktopGuide model={model} toolId={tool} os={os} />}
    </div>
  );
}
